// Generic stable object hashing to detect changes in persisted cache
// - Stable stringify: sorts keys recursively so object order doesn't affect hash
// - Hash: fast FNV-1a 32-bit implementation, returns hex string

function stableStringify(value: unknown): string {
  const seen = new WeakSet();

  const stringify = (val: any): string => {
    if (val === null || typeof val !== "object") return JSON.stringify(val);
    if (seen.has(val)) return '"[Circular]"';
    seen.add(val);

    if (Array.isArray(val)) {
      return `[${val.map((v) => stringify(v)).join(",")}]`;
    }

    const keys = Object.keys(val).sort();
    const entries = keys.map((k) => `${JSON.stringify(k)}:${stringify(val[k])}`);
    return `{${entries.join(",")}}`;
  };

  return stringify(value);
}

export function hashObject(value: unknown): string {
  const str = stableStringify(value);
  // FNV-1a 32-bit
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0;
  }
  return ("0000000" + hash.toString(16)).slice(-8);
}

// Convenience for picking only the fields we care about when hashing
export function hashPicked<T extends object>(obj: T, keys: (keyof T)[]): string {
  const snapshot = keys.reduce((acc, k) => {
    (acc as any)[k as string] = (obj as any)[k as string];
    return acc;
  }, {} as Partial<T>);
  return hashObject(snapshot);
}